import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignInButton, SignUpButton, useUser } from '@clerk/clerk-react';
import { colors } from '../theme';

// Render the (dark) brand mark light, to sit on the dark panel.
const lightLogo = { height: 48, filter: 'brightness(0) invert(1)', opacity: 0.95 };

// Internal vs external is decided by the email domain — North Invent staff sign
// in with an @northinvent address. We only know the email after sign-in, so the
// internal tools are only ever shown to confirmed @northinvent users.
export function isNorthInventEmail(user) {
  const email = user?.primaryEmailAddress?.emailAddress
    || user?.emailAddresses?.[0]?.emailAddress || '';
  const domain = (email.toLowerCase().split('@')[1] || '');
  return domain.includes('northinvent');
}

// External-facing tools (work without — or with — an account).
const EXTERNAL_FEATURES = [
  { icon: '🔍', title: 'Device page', desc: 'Look up a device by service tag — warranty, status and history.', to: '/' },
  { icon: '🌊', title: 'Wave configurator', desc: 'Configure a Wave product and get the matching part number.', to: '/wave-configurator' },
];

// Internal tools — only shown to signed-in @northinvent users.
const INTERNAL_FEATURES = [
  { icon: '🔍', title: 'UID / device lookup', desc: 'Find any device by UID or service tag.', to: '/' },
  { icon: '🗂', title: 'Product Metadata', desc: 'Families, specs, links and the printable sales sheet.', to: '/products' },
  { icon: '📈', title: 'Return stats', desc: 'RMA return rates and warranty reporting.', to: '/rma-report' },
  { icon: '🧰', title: 'RMA process', desc: 'Work RMA cases through the dashboard.', to: '/rmas' },
  { icon: '🏭', title: 'Production output', desc: 'Build orders and live production status.', to: '/production-status' },
  { icon: '✅', title: 'QA & Quality', desc: 'Documents, process flows and the QMS hub.', to: '/quality' },
];

function FeatureCard({ icon, title, desc, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        textAlign: 'left', cursor: 'pointer', display: 'flex', gap: '0.8rem', alignItems: 'flex-start', width: '100%',
        padding: '1rem', borderRadius: '12px', transition: 'all .15s',
        background: hover ? 'rgba(70,187,137,0.08)' : colors.surface,
        border: `1px solid ${hover ? colors.accent : colors.panelBorder}`,
      }}
    >
      <span style={{ fontSize: '1.5rem', lineHeight: 1 }}>{icon}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
        <span style={{ fontWeight: 700, color: colors.textStrong }}>{title}</span>
        <span style={{ fontSize: '0.85rem', color: colors.textMuted, lineHeight: 1.45 }}>{desc}</span>
      </span>
    </button>
  );
}

export default function WelcomeOnboarding() {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const internal = isSignedIn && isNorthInventEmail(user);
  const firstName = user?.firstName || '';
  return (
    <div className="ni-page">
      <div className="ni-shell ni-shell--narrow">
        <div className="ni-panel" style={{ maxWidth: 600, margin: '2.5rem auto', padding: '2rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <img src="/north-invent-logo.svg" alt="North Invent" style={lightLogo} />
          </div>

          {!isLoaded ? null : isSignedIn ? (
            <>
              <h1 style={titleStyle}>Welcome{firstName ? `, ${firstName}` : ''} 👋</h1>
              <p style={subStyle}>
                {internal
                  ? 'Here are the internal tools to get you started — there’s plenty more across the app.'
                  : 'Here’s what you can do with your North Invent account.'}
              </p>
              <div style={cardGrid}>
                {(internal ? INTERNAL_FEATURES : EXTERNAL_FEATURES).map(f => (
                  <FeatureCard key={f.title} {...f} onClick={() => navigate(f.to)} />
                ))}
              </div>
            </>
          ) : (
            <>
              <h1 style={titleStyle}>Welcome to North Invent</h1>
              <p style={subStyle}>Look up a device or configure a product — or sign in for the full toolset.</p>
              <div style={cardGrid}>
                {EXTERNAL_FEATURES.map(f => <FeatureCard key={f.title} {...f} onClick={() => navigate(f.to)} />)}
              </div>

              <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <SignInButton mode="modal" fallbackRedirectUrl="/welcome">
                  <button className="ni-button ni-button--primary">Sign in</button>
                </SignInButton>
                <SignUpButton mode="modal" fallbackRedirectUrl="/welcome">
                  <button className="ni-button ni-button--ghost">Create account</button>
                </SignUpButton>
              </div>
            </>
          )}

          <div style={{ textAlign: 'center' }}>
            <Link to="/" style={{ fontSize: '0.8rem', color: colors.textMuted, textDecoration: 'none' }}>← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const titleStyle = { margin: 0, textAlign: 'center', fontSize: '1.35rem', color: colors.textStrong };
const subStyle = { margin: 0, textAlign: 'center', color: colors.textMuted, lineHeight: 1.55 };
const cardGrid = { display: 'flex', flexDirection: 'column', gap: '0.6rem' };
